import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { supabase } from '../../lib/supabase'
import { invokeEdgeFunction } from '../../lib/supabaseEdge'
import { formatCfgUpdated, LLM_PROVIDERS } from '../../lib/basecampAgentCards'
import {
  clampMaxOutputTokens,
  findDuplicatePromptVersion,
  limitsFromModelRow,
  normalizeForDedup,
} from '../../lib/llmModelGenerationParams'
import GenerationParamSlider from './GenerationParamSlider'
import ConfirmDialog from '../ConfirmDialog'
import { loadKbAgentBehaviorConfig, saveKbAgentBehaviorConfig } from '../../lib/kbAgentAdminSettings'

const KNOWLEDGE_ASSISTANT_KEY = 'knowledge_assistant'

const modalOverlayStyle = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(15, 23, 42, 0.45)',
  zIndex: 1000,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 20,
  boxSizing: 'border-box',
}

const modalPanelStyle = {
  background: '#fff',
  borderRadius: 12,
  maxWidth: 720,
  width: '100%',
  maxHeight: '90vh',
  overflow: 'hidden',
  display: 'flex',
  flexDirection: 'column',
  boxShadow: '0 25px 50px -12px rgba(0,0,0,0.25)',
  border: '1px solid #e2e8f0',
}

const tabRowStyle = {
  display: 'flex',
  borderBottom: '1px solid #e2e8f0',
  background: '#f8fafc',
}

function tabButtonStyle(active) {
  return {
    flex: 1,
    padding: '12px 10px',
    border: 'none',
    borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
    background: active ? '#fff' : 'transparent',
    color: active ? '#0f172a' : '#64748b',
    fontWeight: active ? 700 : 500,
    fontSize: 13,
    cursor: 'pointer',
    marginBottom: -1,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 6,
  }
}

const fieldLabel = {
  display: 'block',
  fontSize: 12,
  fontWeight: 600,
  color: '#475569',
  marginBottom: 6,
}

const sectionTitle = {
  margin: '0 0 12px',
  fontSize: 13,
  fontWeight: 700,
  color: '#0f172a',
  letterSpacing: '-0.01em',
}

const inputStyle = {
  width: '100%',
  padding: '8px 10px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  boxSizing: 'border-box',
  fontSize: 14,
}

const secondaryBtn = {
  padding: '9px 16px',
  borderRadius: 8,
  border: '1px solid #cbd5e1',
  background: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}

const primaryBtn = {
  padding: '9px 16px',
  borderRadius: 8,
  border: 'none',
  background: '#2563eb',
  color: '#fff',
  fontWeight: 600,
  fontSize: 13,
  cursor: 'pointer',
}

const TABS = [
  { id: 'basic', label: 'Basic Info' },
  { id: 'model', label: 'Model Config' },
  { id: 'datasource', label: 'Data Source' },
]

const BEHAVIOR_TAB = { id: 'behavior', label: 'Behavior' }

function providerHasKey(settings, providerId) {
  if (!settings) return false
  if (providerId === 'openai') return Boolean(String(settings.openai_api_key || '').trim())
  if (providerId === 'anthropic') return Boolean(String(settings.anthropic_api_key || '').trim())
  if (providerId === 'gemini') return Boolean(String(settings.gemini_api_key || '').trim())
  return false
}

function defaultModelForProvider(settings, providerId) {
  if (!settings) return ''
  if (providerId === 'openai') return settings.openai_model || ''
  if (providerId === 'anthropic') return settings.anthropic_model || ''
  if (providerId === 'gemini') return settings.gemini_model || ''
  return ''
}

function snapshotBasic(displayName, description, isActive) {
  return {
    displayName: (displayName || '').trim(),
    description: (description || '').trim(),
    isActive: Boolean(isActive),
  }
}

function snapshotModel(state) {
  return {
    llmProvider: state.llmProvider || '',
    llmModel: (state.llmModel || '').trim(),
    systemPrompt: (state.systemPrompt || '').trim(),
    maxTokens: state.useDefaultMaxTokens ? null : state.maxTokensValue,
    useDefaultMaxTokens: Boolean(state.useDefaultMaxTokens),
    temperature: state.useDefaultTemperature ? null : state.temperatureValue,
    useDefaultTemperature: Boolean(state.useDefaultTemperature),
    selectedVersionId: state.selectedVersionId || null,
  }
}

function versionToDraftFields(ver) {
  if (!ver) return { systemPrompt: '', maxTokens: null, temperature: null }
  return {
    systemPrompt: (ver.system_prompt || '').trim(),
    maxTokens:
      ver.max_output_tokens != null && Number.isFinite(Number(ver.max_output_tokens))
        ? Math.floor(Number(ver.max_output_tokens))
        : null,
    temperature:
      ver.temperature != null && Number.isFinite(Number(ver.temperature)) ? Number(ver.temperature) : null,
  }
}

function draftMatchesVersion(draft, ver) {
  const vf = versionToDraftFields(ver)
  return (
    draft.systemPrompt === vf.systemPrompt &&
    draft.maxTokens === vf.maxTokens &&
    draft.temperature === vf.temperature
  )
}

function TabLabel({ tab, isDirty }) {
  return (
    <>
      {tab.label}
      {isDirty ? (
        <span
          style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#f59e0b',
            flexShrink: 0,
          }}
          title="Unsaved changes"
        />
      ) : null}
    </>
  )
}

export default function AgentEditModal({ agentKey, cfgRow, onClose, onSaved, setPageMsg, setPageErr }) {
  const [activeTab, setActiveTab] = useState('basic')
  const [busy, setBusy] = useState(false)
  const [modalMsg, setModalMsg] = useState('')
  const [modalErr, setModalErr] = useState('')
  const [duplicateModalVersion, setDuplicateModalVersion] = useState(null)
  const [discardConfirmOpen, setDiscardConfirmOpen] = useState(false)
  const prevEffectiveModelRef = useRef(null)

  const [baselineBasic, setBaselineBasic] = useState(null)
  const [baselineModel, setBaselineModel] = useState(null)

  const [displayName, setDisplayName] = useState('')
  const [description, setDescription] = useState('')
  const [isActive, setIsActive] = useState(true)

  const [llmProvider, setLlmProvider] = useState('')
  const [llmModel, setLlmModel] = useState('')
  const [maxTokensValue, setMaxTokensValue] = useState(null)
  const [useDefaultMaxTokens, setUseDefaultMaxTokens] = useState(true)
  const [temperatureValue, setTemperatureValue] = useState(null)
  const [useDefaultTemperature, setUseDefaultTemperature] = useState(true)
  const [systemPrompt, setSystemPrompt] = useState('')
  const [versions, setVersions] = useState([])
  const [selectedVersionId, setSelectedVersionId] = useState(null)
  const [activeVersionId, setActiveVersionId] = useState(null)

  const [integrationSettings, setIntegrationSettings] = useState(null)
  const [modelList, setModelList] = useState([])
  const [modelsLoading, setModelsLoading] = useState(false)
  const [modelsErr, setModelsErr] = useState('')

  const [dataSources, setDataSources] = useState([])
  const [expandedSourceId, setExpandedSourceId] = useState(null)

  const [baselineBehavior, setBaselineBehavior] = useState(null)
  const [behaviorDraft, setBehaviorDraft] = useState(null)

  const isKnowledgeAssistant = agentKey === KNOWLEDGE_ASSISTANT_KEY
  const modalTabs = useMemo(
    () => (isKnowledgeAssistant ? [...TABS, BEHAVIOR_TAB] : TABS),
    [isKnowledgeAssistant]
  )

  const currentModelSnapshot = useMemo(
    () =>
      snapshotModel({
        llmProvider,
        llmModel,
        systemPrompt,
        maxTokensValue,
        useDefaultMaxTokens,
        temperatureValue,
        useDefaultTemperature,
        selectedVersionId,
      }),
    [
      llmProvider,
      llmModel,
      systemPrompt,
      maxTokensValue,
      useDefaultMaxTokens,
      temperatureValue,
      useDefaultTemperature,
      selectedVersionId,
    ]
  )

  const dirtyBasic = useMemo(() => {
    if (!baselineBasic) return false
    const cur = snapshotBasic(displayName, description, isActive)
    return (
      cur.displayName !== baselineBasic.displayName ||
      cur.description !== baselineBasic.description ||
      cur.isActive !== baselineBasic.isActive
    )
  }, [baselineBasic, displayName, description, isActive])

  const dirtyModel = useMemo(() => {
    if (!baselineModel) return false
    const cur = currentModelSnapshot
    return (
      cur.llmProvider !== baselineModel.llmProvider ||
      cur.llmModel !== baselineModel.llmModel ||
      cur.systemPrompt !== baselineModel.systemPrompt ||
      cur.maxTokens !== baselineModel.maxTokens ||
      cur.useDefaultMaxTokens !== baselineModel.useDefaultMaxTokens ||
      cur.temperature !== baselineModel.temperature ||
      cur.useDefaultTemperature !== baselineModel.useDefaultTemperature ||
      cur.selectedVersionId !== baselineModel.selectedVersionId
    )
  }, [baselineModel, currentModelSnapshot])

  const dirtyBehavior = useMemo(() => {
    if (!baselineBehavior || !behaviorDraft) return false
    return (
      behaviorDraft.agent_chat_enabled !== baselineBehavior.agent_chat_enabled ||
      behaviorDraft.agent_max_turns_per_hour !== baselineBehavior.agent_max_turns_per_hour ||
      behaviorDraft.agent_max_tool_iterations !== baselineBehavior.agent_max_tool_iterations ||
      behaviorDraft.agent_max_history_turns !== baselineBehavior.agent_max_history_turns ||
      behaviorDraft.agent_tool_result_max_chars !== baselineBehavior.agent_tool_result_max_chars
    )
  }, [baselineBehavior, behaviorDraft])

  const activeVersion = useMemo(
    () => versions.find((v) => v.id === activeVersionId) || null,
    [versions, activeVersionId]
  )

  const loadVersions = useCallback(async () => {
    const { data, error } = await supabase
      .from('ai_agent_prompt_versions')
      .select('*')
      .eq('agent_key', agentKey)
      .order('version_number', { ascending: false })
    if (error) throw error
    setVersions(data || [])
    return data || []
  }, [agentKey])

  const loadDataSources = useCallback(async () => {
    const { data, error } = await supabase
      .from('ai_agent_data_sources')
      .select('*')
      .eq('agent_key', agentKey)
      .order('sort_order')
    if (error) throw error
    setDataSources(data || [])
  }, [agentKey])

  const applyVersionToEditor = useCallback((ver) => {
    if (!ver) return
    setSelectedVersionId(ver.id)
    setSystemPrompt(ver.system_prompt || '')
    if (ver.max_output_tokens != null && Number.isFinite(Number(ver.max_output_tokens))) {
      setUseDefaultMaxTokens(false)
      setMaxTokensValue(Math.floor(Number(ver.max_output_tokens)))
    } else {
      setUseDefaultMaxTokens(true)
      setMaxTokensValue(null)
    }
    if (ver.temperature != null && Number.isFinite(Number(ver.temperature))) {
      setUseDefaultTemperature(false)
      setTemperatureValue(Number(ver.temperature))
    } else {
      setUseDefaultTemperature(true)
      setTemperatureValue(null)
    }
  }, [])

  const setBaselinesFromState = useCallback(
    (vers, activeVerId) => {
      setBaselineBasic(snapshotBasic(displayName, description, isActive))
      setBaselineModel(
        snapshotModel({
          llmProvider,
          llmModel,
          systemPrompt,
          maxTokensValue,
          useDefaultMaxTokens,
          temperatureValue,
          useDefaultTemperature,
          selectedVersionId: activeVerId ?? selectedVersionId,
        })
      )
    },
    [
      displayName,
      description,
      isActive,
      llmProvider,
      llmModel,
      systemPrompt,
      maxTokensValue,
      useDefaultMaxTokens,
      temperatureValue,
      useDefaultTemperature,
      selectedVersionId,
    ]
  )

  useEffect(() => {
    if (!cfgRow) return
    setDisplayName(cfgRow.display_name || agentKey)
    setDescription(cfgRow.description || '')
    setIsActive(cfgRow.is_active !== false)
    setLlmProvider(cfgRow.llm_provider || '')
    setLlmModel(cfgRow.llm_model || '')
    setActiveVersionId(cfgRow.active_prompt_version_id || null)

    void (async () => {
      try {
        const vers = await loadVersions()
        const activeVer = vers.find((v) => v.id === cfgRow.active_prompt_version_id) || vers[0]
        if (activeVer) {
          applyVersionToEditor(activeVer)
          setBaselineBasic(snapshotBasic(cfgRow.display_name || agentKey, cfgRow.description || '', cfgRow.is_active !== false))
          setBaselineModel(
            snapshotModel({
              llmProvider: cfgRow.llm_provider || '',
              llmModel: cfgRow.llm_model || '',
              systemPrompt: activeVer.system_prompt || '',
              maxTokensValue:
                activeVer.max_output_tokens != null
                  ? Math.floor(Number(activeVer.max_output_tokens))
                  : null,
              useDefaultMaxTokens: activeVer.max_output_tokens == null,
              temperatureValue:
                activeVer.temperature != null ? Number(activeVer.temperature) : null,
              useDefaultTemperature: activeVer.temperature == null,
              selectedVersionId: activeVer.id,
            })
          )
        } else {
          setSystemPrompt(cfgRow.system_prompt || '')
          if (cfgRow.max_output_tokens != null) {
            setUseDefaultMaxTokens(false)
            setMaxTokensValue(Math.floor(Number(cfgRow.max_output_tokens)))
          } else {
            setUseDefaultMaxTokens(true)
            setMaxTokensValue(null)
          }
          setUseDefaultTemperature(true)
          setTemperatureValue(null)
          setBaselinesFromState(vers, null)
        }
        await loadDataSources()
        if (agentKey === KNOWLEDGE_ASSISTANT_KEY) {
          const behavior = await loadKbAgentBehaviorConfig()
          setBehaviorDraft(behavior)
          setBaselineBehavior(behavior)
        } else {
          setBehaviorDraft(null)
          setBaselineBehavior(null)
        }
      } catch (e) {
        setModalErr(e?.message || String(e))
        setPageErr(e?.message || String(e))
      }
    })()
    // eslint-disable-next-line react-hooks/exhaustive-deps -- initial load from cfgRow only
  }, [agentKey, cfgRow])

  useEffect(() => {
    void (async () => {
      try {
        const res = await invokeEdgeFunction('race-tracker-settings', { method: 'GET' })
        const data = await res.json()
        if (!res.ok) return
        setIntegrationSettings(data.settings || data)
      } catch {
        /* optional */
      }
    })()
  }, [])

  const effectiveProvider = llmProvider || integrationSettings?.primary_llm_provider || 'anthropic'
  const effectiveModelId =
    llmModel?.trim() || defaultModelForProvider(integrationSettings, effectiveProvider) || ''

  const selectedModelRow = useMemo(
    () => modelList.find((m) => m.id === effectiveModelId) || null,
    [modelList, effectiveModelId]
  )

  const generationLimits = useMemo(
    () => limitsFromModelRow(effectiveProvider, effectiveModelId, selectedModelRow || {}),
    [effectiveProvider, effectiveModelId, selectedModelRow]
  )

  useEffect(() => {
    const key = `${effectiveProvider}:${effectiveModelId}`
    if (!effectiveModelId) return
    if (prevEffectiveModelRef.current == null) {
      prevEffectiveModelRef.current = key
      return
    }
    if (prevEffectiveModelRef.current === key) return
    prevEffectiveModelRef.current = key
    setUseDefaultMaxTokens(true)
    setMaxTokensValue(null)
    setUseDefaultTemperature(true)
    setTemperatureValue(null)
  }, [effectiveProvider, effectiveModelId])

  const resolvedMaxForSave = useMemo(() => {
    if (useDefaultMaxTokens) return null
    return clampMaxOutputTokens(maxTokensValue, generationLimits)
  }, [useDefaultMaxTokens, maxTokensValue, generationLimits])

  const resolvedTempForSave = useMemo(() => {
    if (useDefaultTemperature || !generationLimits.temperature.supported) return null
    return temperatureValue
  }, [useDefaultTemperature, temperatureValue, generationLimits])

  const dedupParams = useMemo(
    () =>
      normalizeForDedup({
        systemPrompt,
        maxOut: resolvedMaxForSave,
        temperature: resolvedTempForSave,
        useDefaultMaxTokens,
        useDefaultTemperature,
        limits: generationLimits,
      }),
    [
      systemPrompt,
      resolvedMaxForSave,
      resolvedTempForSave,
      useDefaultMaxTokens,
      useDefaultTemperature,
      generationLimits,
    ]
  )

  const duplicateVersion = useMemo(
    () =>
      findDuplicatePromptVersion(
        versions,
        dedupParams.prompt,
        dedupParams.maxOut,
        dedupParams.temperature,
        generationLimits
      ),
    [versions, dedupParams, generationLimits]
  )

  const routingDirty = useMemo(() => {
    if (!baselineModel) return false
    return (
      currentModelSnapshot.llmProvider !== baselineModel.llmProvider ||
      currentModelSnapshot.llmModel !== baselineModel.llmModel
    )
  }, [baselineModel, currentModelSnapshot])

  const promptContentDirty = useMemo(() => {
    if (!baselineModel) return false
    return (
      currentModelSnapshot.systemPrompt !== baselineModel.systemPrompt ||
      currentModelSnapshot.maxTokens !== baselineModel.maxTokens ||
      currentModelSnapshot.useDefaultMaxTokens !== baselineModel.useDefaultMaxTokens ||
      currentModelSnapshot.temperature !== baselineModel.temperature ||
      currentModelSnapshot.useDefaultTemperature !== baselineModel.useDefaultTemperature
    )
  }, [baselineModel, currentModelSnapshot])

  const routingSaveDisabled = busy || !routingDirty
  const promptSaveDisabled = busy || !(systemPrompt || '').trim()

  const fetchModels = useCallback(async () => {
    setModelsErr('')
    setModelsLoading(true)
    try {
      const res = await invokeEdgeFunction('integration-probe', {
        method: 'POST',
        body: JSON.stringify({ action: 'list_models', provider: effectiveProvider }),
      })
      const data = await res.json()
      if (!res.ok) throw new Error(data.error || 'Failed to load models')
      setModelList(data.models || [])
      if (data.warning) setModelsErr(data.warning)
    } catch (e) {
      setModelsErr(e?.message || String(e))
      setModelList([])
    } finally {
      setModelsLoading(false)
    }
  }, [effectiveProvider])

  useEffect(() => {
    if (activeTab === 'model' && modelList.length === 0 && !modelsLoading) {
      void fetchModels()
    }
  }, [activeTab, modelList.length, modelsLoading, fetchModels])

  const requestClose = () => {
    if (dirtyBasic || dirtyModel || dirtyBehavior) {
      setDiscardConfirmOpen(true)
      return
    }
    onClose()
  }

  const confirmDiscardClose = () => {
    setDiscardConfirmOpen(false)
    onClose()
  }

  const clearModalFeedback = () => {
    setModalMsg('')
    setModalErr('')
  }

  const saveBehaviorConfig = async () => {
    if (!behaviorDraft) return
    setBusy(true)
    clearModalFeedback()
    setPageErr('')
    setPageMsg('')
    try {
      const saved = await saveKbAgentBehaviorConfig(behaviorDraft)
      setBehaviorDraft(saved)
      setBaselineBehavior(saved)
      const msg = 'Behavior settings saved.'
      setModalMsg(msg)
      setPageMsg(msg)
    } catch (e) {
      const m = e?.message || String(e)
      setModalErr(m)
      setPageErr(m)
    } finally {
      setBusy(false)
    }
  }

  const saveBasicInfo = async () => {
    const name = (displayName || '').trim()
    if (!name) {
      setModalErr('Display name is required.')
      return
    }
    setBusy(true)
    clearModalFeedback()
    setPageErr('')
    setPageMsg('')
    try {
      const { data, error } = await supabase
        .from('basecamp_ai_agent_configs')
        .update({
          display_name: name,
          description: (description || '').trim() || null,
          is_active: isActive,
        })
        .eq('agent_key', agentKey)
        .select('*')
        .single()
      if (error) throw error
      setBaselineBasic(snapshotBasic(displayName, description, isActive))
      setModalMsg('Basic info saved.')
      setPageMsg('Basic info saved.')
      onSaved(data)
    } catch (e) {
      const m = e?.message || String(e)
      setModalErr(m)
      setPageErr(m)
    } finally {
      setBusy(false)
    }
  }

  const saveModelConfig = async () => {
    setBusy(true)
    clearModalFeedback()
    setPageErr('')
    setPageMsg('')
    try {
      const { data, error } = await supabase
        .from('basecamp_ai_agent_configs')
        .update({
          llm_provider: llmProvider || null,
          llm_model: llmModel?.trim() || null,
        })
        .eq('agent_key', agentKey)
        .select('*')
        .single()
      if (error) throw error
      setBaselineModel((prev) =>
        prev
          ? {
              ...prev,
              llmProvider: llmProvider || '',
              llmModel: (llmModel || '').trim(),
            }
          : snapshotModel({
              llmProvider,
              llmModel,
              systemPrompt,
              maxTokensValue,
              useDefaultMaxTokens,
              temperatureValue,
              useDefaultTemperature,
              selectedVersionId,
            })
      )
      const msg = 'Model routing saved.'
      setModalMsg(msg)
      setPageMsg(msg)
      onSaved(data)
    } catch (e) {
      const m = e?.message || String(e)
      setModalErr(m)
      setPageErr(m)
    } finally {
      setBusy(false)
    }
  }

  const finishPromptSave = async (nextActiveId, versList, trimmed, maxOut, tempOut, successMsg) => {
    const activeVer = versList.find((v) => v.id === nextActiveId)
    const { data, error } = await supabase
      .from('basecamp_ai_agent_configs')
      .update({
        active_prompt_version_id: nextActiveId,
        system_prompt: activeVer?.system_prompt || trimmed,
        max_output_tokens: activeVer?.max_output_tokens ?? maxOut,
        temperature: activeVer?.temperature ?? tempOut,
      })
      .eq('agent_key', agentKey)
      .select('*')
      .single()
    if (error) throw error

    setActiveVersionId(nextActiveId)
    if (activeVer) applyVersionToEditor(activeVer)

    setBaselineModel(
      snapshotModel({
        llmProvider,
        llmModel,
        systemPrompt,
        maxTokensValue,
        useDefaultMaxTokens,
        temperatureValue,
        useDefaultTemperature,
        selectedVersionId: nextActiveId,
      })
    )

    setModalMsg(successMsg)
    setPageMsg(successMsg)
    onSaved(data)
  }

  const activateDuplicateVersion = async (dup) => {
    if (!dup) return
    setDuplicateModalVersion(null)
    setBusy(true)
    clearModalFeedback()
    setPageErr('')
    setPageMsg('')
    try {
      const trimmed = (systemPrompt || '').trim()
      const maxOut = resolvedMaxForSave
      const tempOut = resolvedTempForSave
      await finishPromptSave(
        dup.id,
        versions,
        trimmed,
        maxOut,
        tempOut,
        `Using existing v${dup.version_number} as active for production.`
      )
    } catch (e) {
      const m = e?.message || String(e)
      setModalErr(m)
      setPageErr(m)
    } finally {
      setBusy(false)
    }
  }

  const savePromptVersion = async () => {
    const trimmed = (systemPrompt || '').trim()
    if (!trimmed) {
      setModalErr('System prompt cannot be empty.')
      return
    }

    const maxOut = resolvedMaxForSave
    const tempOut = resolvedTempForSave
    const draft = currentModelSnapshot
    const normalized = dedupParams

    const dup = findDuplicatePromptVersion(
      versions,
      normalized.prompt,
      normalized.maxOut,
      normalized.temperature,
      generationLimits
    )
    if (dup) {
      setDuplicateModalVersion(dup)
      return
    }

    setBusy(true)
    clearModalFeedback()
    setPageErr('')
    setPageMsg('')
    try {
      let nextActiveId = activeVersionId
      let versList = versions

      const selectedVer = versList.find((v) => v.id === selectedVersionId)
      if (
        selectedVer &&
        draftMatchesVersion(draft, selectedVer) &&
        selectedVersionId !== activeVersionId
      ) {
        nextActiveId = selectedVersionId
      } else if (!draftMatchesVersion(draft, activeVersion)) {
        const { data: session } = await supabase.auth.getSession()
        const userId = session?.session?.user?.id ?? null
        const maxVer = versList.reduce((m, v) => Math.max(m, v.version_number || 0), 0)
        const { data: newVer, error: insErr } = await supabase
          .from('ai_agent_prompt_versions')
          .insert({
            agent_key: agentKey,
            version_number: maxVer + 1,
            system_prompt: trimmed,
            max_output_tokens: maxOut,
            temperature: tempOut,
            created_by: userId,
            label: `v${maxVer + 1}`,
          })
          .select('*')
          .single()
        if (insErr) throw insErr
        nextActiveId = newVer.id
        versList = await loadVersions()
      } else if (selectedVersionId && selectedVersionId !== activeVersionId) {
        nextActiveId = selectedVersionId
      }

      const activeVer = versList.find((v) => v.id === nextActiveId)
      const verLabel = activeVer?.version_number ?? '?'
      await finishPromptSave(
        nextActiveId,
        versList,
        trimmed,
        maxOut,
        tempOut,
        `Prompt v${verLabel} saved and active for cron and dry-runs.`
      )
    } catch (e) {
      const m = e?.message || String(e)
      setModalErr(m)
      setPageErr(m)
    } finally {
      setBusy(false)
    }
  }

  const onVersionSelect = (e) => {
    const id = e.target.value
    setSelectedVersionId(id || null)
    const ver = versions.find((v) => v.id === id)
    if (ver) applyVersionToEditor(ver)
  }

  const title = displayName || cfgRow?.display_name || agentKey
  const stepOrder = cfgRow?.step_order

  return (
    <div role="presentation" onClick={requestClose} style={modalOverlayStyle}>
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="edit-agent-dialog-title"
        onClick={(e) => e.stopPropagation()}
        style={{ ...modalPanelStyle, position: 'relative' }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            padding: '16px 20px',
            borderBottom: '1px solid #e2e8f0',
            flexShrink: 0,
          }}
        >
          <h2 id="edit-agent-dialog-title" style={{ margin: 0, fontSize: 17, fontWeight: 700, color: '#0f172a' }}>
            Edit — {title}
          </h2>
          <button
            type="button"
            onClick={requestClose}
            style={{
              border: 'none',
              background: '#f1f5f9',
              width: 36,
              height: 36,
              borderRadius: 8,
              cursor: 'pointer',
              fontSize: 18,
              color: '#475569',
            }}
            aria-label="Close"
          >
            ×
          </button>
        </div>

        <div role="tablist" aria-label="Agent settings" style={tabRowStyle}>
          {modalTabs.map((t) => (
            <button
              key={t.id}
              type="button"
              role="tab"
              aria-selected={activeTab === t.id}
              id={`agent-tab-${t.id}`}
              aria-controls={`agent-panel-${t.id}`}
              style={tabButtonStyle(activeTab === t.id)}
              onClick={() => setActiveTab(t.id)}
            >
              <TabLabel
                tab={t}
                isDirty={
                  t.id === 'basic'
                    ? dirtyBasic
                    : t.id === 'model'
                      ? dirtyModel
                      : t.id === 'behavior'
                        ? dirtyBehavior
                        : false
                }
              />
            </button>
          ))}
        </div>

        {(modalMsg || modalErr) && (
          <div
            style={{
              padding: '10px 20px',
              borderBottom: '1px solid #e2e8f0',
              background: modalErr ? '#fef2f2' : '#f0fdf4',
              fontSize: 13,
              color: modalErr ? '#b91c1c' : '#15803d',
              flexShrink: 0,
            }}
          >
            {modalErr || modalMsg}
          </div>
        )}

        <div style={{ flex: 1, overflow: 'auto', padding: 20, minHeight: 320 }}>
          {activeTab === 'basic' ? (
            <div role="tabpanel" id="agent-panel-basic" aria-labelledby="agent-tab-basic">
              <label style={fieldLabel}>Display name</label>
              <input
                style={{ ...inputStyle, marginBottom: 14 }}
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
              />
              <label style={fieldLabel}>Description</label>
              <textarea
                rows={4}
                style={{ ...inputStyle, marginBottom: 14, resize: 'vertical', fontFamily: 'inherit' }}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
              />
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 8px' }}>
                Agent key: <code>{agentKey}</code>
                {stepOrder != null && agentKey !== KNOWLEDGE_ASSISTANT_KEY ? ` · Step ${stepOrder}` : null}
              </p>
              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155' }}>
                <input type="checkbox" checked={isActive} onChange={(e) => setIsActive(e.target.checked)} />
                Agent enabled
              </label>
              {isKnowledgeAssistant ? (
                <p style={{ fontSize: 12, color: '#64748b', marginTop: 12, lineHeight: 1.5 }}>
                  User chat lives at{' '}
                  <Link to="/knowledge" style={{ color: '#2563eb', fontWeight: 600 }}>
                    /knowledge
                  </Link>
                  . Search/RAG knobs remain under Knowledge Access.
                </p>
              ) : null}
              <p style={{ fontSize: 12, color: '#94a3b8', marginTop: 12 }}>
                Config updated: {formatCfgUpdated(cfgRow?.updated_at)}
              </p>
            </div>
          ) : null}

          {activeTab === 'model' ? (
            <div role="tabpanel" id="agent-panel-model" aria-labelledby="agent-tab-model">
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 }}>
                API keys are configured under{' '}
                <Link to="/admin/integrations" style={{ color: '#2563eb', fontWeight: 600 }}>
                  Settings → Integrations
                </Link>
                . Leave provider empty to use the global default. Footer Save updates provider and model only;
                use Save prompt for prompt versions.
              </p>

              <h4 style={sectionTitle}>Provider and model</h4>
              <label style={fieldLabel}>Provider override (optional)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
                <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 }}>
                  <input
                    type="radio"
                    name="llmProvider"
                    checked={!llmProvider}
                    onChange={() => {
                      setLlmProvider('')
                      setLlmModel('')
                      setModelList([])
                    }}
                  />
                  Global default
                </label>
                {LLM_PROVIDERS.map((p) => {
                  const hasKey = providerHasKey(integrationSettings, p.id)
                  return (
                    <label
                      key={p.id}
                      style={{
                        fontSize: 13,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 6,
                        opacity: hasKey || !integrationSettings ? 1 : 0.5,
                      }}
                      title={hasKey ? '' : 'No API key configured for this provider'}
                    >
                      <input
                        type="radio"
                        name="llmProvider"
                        checked={llmProvider === p.id}
                        disabled={integrationSettings && !hasKey}
                        onChange={() => {
                          setLlmProvider(p.id)
                          setLlmModel(defaultModelForProvider(integrationSettings, p.id))
                          setModelList([])
                        }}
                      />
                      {p.label}
                    </label>
                  )
                })}
              </div>
              <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', marginBottom: 20, flexWrap: 'wrap' }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={fieldLabel}>Model</label>
                  <select
                    style={inputStyle}
                    value={llmModel}
                    onChange={(e) => setLlmModel(e.target.value)}
                    disabled={!llmProvider && !integrationSettings}
                  >
                    <option value="">
                      {llmProvider
                        ? 'Select model…'
                        : `Default (${defaultModelForProvider(integrationSettings, effectiveProvider) || '—'})`}
                    </option>
                    {modelList.map((m) => (
                      <option key={m.id} value={m.id}>
                        {m.displayName || m.id}
                      </option>
                    ))}
                  </select>
                </div>
                <button
                  type="button"
                  onClick={() => void fetchModels()}
                  disabled={modelsLoading}
                  style={{
                    ...secondaryBtn,
                    cursor: modelsLoading ? 'wait' : 'pointer',
                  }}
                >
                  {modelsLoading ? 'Loading…' : 'Refresh models'}
                </button>
              </div>
              {modelsErr ? <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 16px' }}>{modelsErr}</p> : null}

              <h4 style={sectionTitle}>Generation parameters</h4>
              <label style={fieldLabel}>Prompt version</label>
              <select style={{ ...inputStyle, marginBottom: 8 }} value={selectedVersionId || ''} onChange={onVersionSelect}>
                {versions.length === 0 ? <option value="">No versions yet</option> : null}
                {versions.map((v) => (
                  <option key={v.id} value={v.id}>
                    v{v.version_number}
                    {v.id === activeVersionId ? ' (active)' : ''}
                    {v.label ? ` — ${v.label}` : ''}
                    {v.created_at ? ` · ${formatCfgUpdated(v.created_at)}` : ''}
                  </option>
                ))}
              </select>
              {activeVersion ? (
                <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px' }}>
                  Active in production: v{activeVersion.version_number}
                </p>
              ) : null}

              <GenerationParamSlider
                mode="tokens"
                limits={generationLimits}
                value={maxTokensValue}
                useDefault={useDefaultMaxTokens}
                onUseDefaultChange={(checked) => {
                  setUseDefaultMaxTokens(checked)
                  if (checked) setMaxTokensValue(null)
                  else setMaxTokensValue(generationLimits.maxOutputTokens.default)
                }}
                onChange={setMaxTokensValue}
                label="Max output tokens"
                modelHint={effectiveModelId || effectiveProvider}
              />

              {generationLimits.temperature.supported ? (
                <GenerationParamSlider
                  mode="temperature"
                  limits={generationLimits}
                  value={temperatureValue}
                  useDefault={useDefaultTemperature}
                  onUseDefaultChange={(checked) => {
                    setUseDefaultTemperature(checked)
                    if (checked) setTemperatureValue(null)
                    else setTemperatureValue(generationLimits.temperature.default)
                  }}
                  onChange={setTemperatureValue}
                  label="Temperature"
                  modelHint={effectiveModelId || effectiveProvider}
                />
              ) : null}

              {duplicateVersion && promptContentDirty ? (
                <p style={{ fontSize: 12, color: '#b45309', margin: '0 0 16px' }}>
                  Matches saved v{duplicateVersion.version_number} — change prompt, tokens, or temperature, or use
                  Save prompt to see options.
                </p>
              ) : null}

              <h4 style={{ ...sectionTitle, marginTop: 20 }}>Prompt</h4>
              <label style={fieldLabel}>System prompt</label>
              <textarea
                value={systemPrompt}
                onChange={(e) => setSystemPrompt(e.target.value)}
                rows={12}
                style={{
                  width: '100%',
                  minHeight: 200,
                  padding: 10,
                  borderRadius: 8,
                  border: '1px solid #cbd5e1',
                  fontSize: 12,
                  fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
                  resize: 'vertical',
                  boxSizing: 'border-box',
                }}
              />
              <div style={{ display: 'flex', gap: 10, marginTop: 14, flexWrap: 'wrap' }}>
                <button
                  type="button"
                  disabled={promptSaveDisabled}
                  onClick={() => void savePromptVersion()}
                  style={{
                    ...primaryBtn,
                    opacity: promptSaveDisabled ? 0.55 : 1,
                    cursor: promptSaveDisabled ? 'not-allowed' : 'pointer',
                  }}
                >
                  {busy ? 'Saving…' : 'Save prompt'}
                </button>
              </div>
            </div>
          ) : null}

          {activeTab === 'behavior' && behaviorDraft ? (
            <div role="tabpanel" id="agent-panel-behavior" aria-labelledby="agent-tab-behavior">
              <p style={{ fontSize: 12, color: '#64748b', margin: '0 0 16px', lineHeight: 1.5 }}>
                Operational limits for the in-app chat on{' '}
                <Link to="/knowledge" style={{ color: '#2563eb', fontWeight: 600 }}>
                  /knowledge
                </Link>
                . Prompt and model settings are on the Model Config tab.
              </p>

              <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, color: '#334155', marginBottom: 16 }}>
                <input
                  type="checkbox"
                  checked={behaviorDraft.agent_chat_enabled}
                  onChange={(e) =>
                    setBehaviorDraft((prev) => ({ ...prev, agent_chat_enabled: e.target.checked }))
                  }
                />
                Agent chat enabled (org-wide)
              </label>

              <label style={fieldLabel}>Max turns per user per hour</label>
              <input
                type="number"
                min={1}
                max={500}
                style={{ ...inputStyle, marginBottom: 14, maxWidth: 160 }}
                value={behaviorDraft.agent_max_turns_per_hour}
                onChange={(e) =>
                  setBehaviorDraft((prev) => ({
                    ...prev,
                    agent_max_turns_per_hour: Number(e.target.value),
                  }))
                }
              />

              <label style={fieldLabel}>Max tool iterations per message</label>
              <input
                type="number"
                min={1}
                max={50}
                style={{ ...inputStyle, marginBottom: 14, maxWidth: 160 }}
                value={behaviorDraft.agent_max_tool_iterations}
                onChange={(e) =>
                  setBehaviorDraft((prev) => ({
                    ...prev,
                    agent_max_tool_iterations: Number(e.target.value),
                  }))
                }
              />

              <label style={fieldLabel}>Max history turns (context window)</label>
              <input
                type="number"
                min={1}
                max={200}
                style={{ ...inputStyle, marginBottom: 14, maxWidth: 160 }}
                value={behaviorDraft.agent_max_history_turns}
                onChange={(e) =>
                  setBehaviorDraft((prev) => ({
                    ...prev,
                    agent_max_history_turns: Number(e.target.value),
                  }))
                }
              />

              <label style={fieldLabel}>Tool result max characters</label>
              <input
                type="number"
                min={1000}
                max={100000}
                step={500}
                style={{ ...inputStyle, marginBottom: 14, maxWidth: 200 }}
                value={behaviorDraft.agent_tool_result_max_chars}
                onChange={(e) =>
                  setBehaviorDraft((prev) => ({
                    ...prev,
                    agent_tool_result_max_chars: Number(e.target.value),
                  }))
                }
              />
            </div>
          ) : null}

          {activeTab === 'datasource' ? (
            <div role="tabpanel" id="agent-panel-datasource" aria-labelledby="agent-tab-datasource">
              <p style={{ fontSize: 13, color: '#64748b', margin: '0 0 16px', lineHeight: 1.55 }}>
                Data bindings are defined by the pipeline implementation (read-only). Contact engineering to add
                sources.
              </p>
              {dataSources.length === 0 ? (
                <p style={{ color: '#94a3b8', fontSize: 13 }}>No data sources documented for this agent.</p>
              ) : (
                <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                  {dataSources.map((src) => {
                    const open = expandedSourceId === src.id
                    const details = src.details_json || {}
                    return (
                      <li
                        key={src.id}
                        style={{
                          border: '1px solid #e2e8f0',
                          borderRadius: 8,
                          marginBottom: 8,
                          overflow: 'hidden',
                        }}
                      >
                        <button
                          type="button"
                          onClick={() => setExpandedSourceId(open ? null : src.id)}
                          style={{
                            width: '100%',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            padding: '12px 14px',
                            border: 'none',
                            background: open ? '#f1f5f9' : '#fff',
                            cursor: 'pointer',
                            fontSize: 14,
                            fontWeight: 600,
                            color: '#0f172a',
                            textAlign: 'left',
                          }}
                        >
                          <span>
                            {src.table_name}
                            <span style={{ fontWeight: 400, color: '#64748b', marginLeft: 8 }}>{src.role}</span>
                          </span>
                          <span style={{ color: '#94a3b8' }}>{open ? '▾' : '▸'}</span>
                        </button>
                        {open ? (
                          <div
                            style={{
                              padding: '12px 14px',
                              borderTop: '1px solid #e2e8f0',
                              fontSize: 12,
                              color: '#475569',
                            }}
                          >
                            {details.columns ? (
                              <p style={{ margin: '0 0 8px' }}>
                                <strong>Columns:</strong>{' '}
                                {Array.isArray(details.columns) ? details.columns.join(', ') : '—'}
                              </p>
                            ) : null}
                            {details.filter ? (
                              <p style={{ margin: '0 0 8px' }}>
                                <strong>Filter:</strong> {details.filter}
                              </p>
                            ) : null}
                            {details.join_context ? (
                              <p style={{ margin: '0 0 8px' }}>
                                <strong>Context:</strong> {details.join_context}
                              </p>
                            ) : null}
                            {details.description ? (
                              <p style={{ margin: '0 0 8px' }}>
                                <strong>Notes:</strong> {details.description}
                              </p>
                            ) : null}
                            {details.edge_reference ? (
                              <p style={{ margin: 0 }}>
                                <strong>Edge:</strong> {details.edge_reference}
                              </p>
                            ) : null}
                          </div>
                        ) : null}
                      </li>
                    )
                  })}
                </ul>
              )}
            </div>
          ) : null}
        </div>

        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 10,
            padding: '14px 20px',
            borderTop: '1px solid #e2e8f0',
            flexShrink: 0,
            justifyContent: 'flex-end',
            alignItems: 'center',
            background: '#f8fafc',
          }}
        >
          {activeTab === 'datasource' ? (
            <button
              type="button"
              disabled={busy}
              onClick={requestClose}
              style={{
                ...secondaryBtn,
                cursor: busy ? 'wait' : 'pointer',
              }}
            >
              Close
            </button>
          ) : (
            <>
              <button
                type="button"
                disabled={busy}
                onClick={requestClose}
                style={{
                  ...secondaryBtn,
                  cursor: busy ? 'wait' : 'pointer',
                }}
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={
                  busy ||
                  (activeTab === 'basic' && !dirtyBasic) ||
                  (activeTab === 'model' && routingSaveDisabled) ||
                  (activeTab === 'behavior' && !dirtyBehavior)
                }
                onClick={() =>
                  void (
                    activeTab === 'basic'
                      ? saveBasicInfo()
                      : activeTab === 'behavior'
                        ? saveBehaviorConfig()
                        : saveModelConfig()
                  )
                }
                style={{
                  ...primaryBtn,
                  opacity:
                    busy ||
                    (activeTab === 'basic' && !dirtyBasic) ||
                    (activeTab === 'model' && routingSaveDisabled) ||
                    (activeTab === 'behavior' && !dirtyBehavior)
                      ? 0.55
                      : 1,
                  cursor:
                    busy ||
                    (activeTab === 'basic' && !dirtyBasic) ||
                    (activeTab === 'model' && routingSaveDisabled) ||
                    (activeTab === 'behavior' && !dirtyBehavior)
                      ? 'not-allowed'
                      : 'pointer',
                }}
              >
                {busy
                  ? 'Saving…'
                  : activeTab === 'model'
                    ? 'Save routing'
                    : activeTab === 'behavior'
                      ? 'Save behavior'
                      : 'Save'}
              </button>
            </>
          )}
        </div>

        {duplicateModalVersion ? (
          <div
            role="alertdialog"
            aria-labelledby="dup-prompt-title"
            aria-describedby="dup-prompt-desc"
            style={{
              position: 'absolute',
              inset: 0,
              background: 'rgba(15, 23, 42, 0.5)',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
              padding: 24,
              zIndex: 10,
            }}
            onClick={() => setDuplicateModalVersion(null)}
          >
            <div
              style={{
                background: '#fff',
                borderRadius: 12,
                padding: '20px 22px',
                maxWidth: 420,
                width: '100%',
                boxShadow: '0 20px 40px rgba(0,0,0,0.2)',
                border: '1px solid #e2e8f0',
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <h3 id="dup-prompt-title" style={{ margin: '0 0 10px', fontSize: 16, color: '#0f172a' }}>
                Prompt already saved
              </h3>
              <p id="dup-prompt-desc" style={{ margin: '0 0 16px', fontSize: 14, color: '#475569', lineHeight: 1.5 }}>
                This prompt already exists as version v{duplicateModalVersion.version_number}
                {duplicateModalVersion.created_at
                  ? ` (saved ${formatCfgUpdated(duplicateModalVersion.created_at)})`
                  : ''}
                . Change the prompt, max tokens, or temperature to save a new version.
              </p>
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', flexWrap: 'wrap' }}>
                <button
                  type="button"
                  style={secondaryBtn}
                  onClick={() => setDuplicateModalVersion(null)}
                >
                  Close
                </button>
                <button
                  type="button"
                  disabled={busy}
                  style={{
                    ...primaryBtn,
                    cursor: busy ? 'wait' : 'pointer',
                  }}
                  onClick={() => void activateDuplicateVersion(duplicateModalVersion)}
                >
                  Use v{duplicateModalVersion.version_number} as active
                </button>
              </div>
            </div>
          </div>
        ) : null}

        <ConfirmDialog
          open={discardConfirmOpen}
          onClose={() => setDiscardConfirmOpen(false)}
          title="Discard unsaved changes?"
          description="Your edits to this agent will be lost."
          confirmLabel="Discard"
          variant="neutral"
          onConfirm={confirmDiscardClose}
        />
      </div>
    </div>
  )
}
